import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const CATS = ["Tentang Bawaslu", "Pengawasan Pemilu", "Pelaporan", "Pengawasan Partisipatif", "Informasi Publik", "Kontak Bawaslu"];
const EMPTY = { category: CATS[0], question: "", answer: "" };

export default function FaqAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/faq").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (f) => { setForm({ category: f.category, question: f.question, answer: f.answer }); setEditId(f.id); setOpen(true); };

  const save = async () => {
    if (!form.question || !form.answer) { toast.error("Pertanyaan dan jawaban wajib diisi"); return; }
    if (editId) await api.put(`/faq/${editId}`, form);
    else await api.post("/faq", form);
    toast.success("FAQ disimpan"); setOpen(false); load();
  };
  const del = async (id) => { await api.delete(`/faq/${id}`); toast.success("FAQ dihapus"); load(); };

  const grouped = CATS.map((c) => ({ cat: c, items: items.filter((f) => f.category === c) })).filter((g) => g.items.length);

  return (
    <div>
      <AdminHeader title="FAQ / Tanya Bawaslu" description="Kelola basis pengetahuan chatbot Tanya Bawaslu."
        action={<Button onClick={openNew} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="add-faq-btn"><Plus className="h-4 w-4 mr-1.5" /> Tambah FAQ</Button>} />

      <div className="space-y-6" data-testid="faq-list">
        {grouped.map((g) => (
          <div key={g.cat}>
            <h3 className="font-bold text-navy mb-2">{g.cat}</h3>
            <Accordion type="single" collapsible className="bg-white border border-slate-200 rounded-xl px-4">
              {g.items.map((f) => (
                <AccordionItem key={f.id} value={f.id}>
                  <AccordionTrigger className="text-sm font-semibold text-navy hover:no-underline">{f.question}</AccordionTrigger>
                  <AccordionContent>
                    <p className="text-slate-600">{f.answer}</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => openEdit(f)} data-testid={`edit-faq-${f.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => del(f.id)} className="text-brandred border-red-200"><Trash2 className="h-3.5 w-3.5 mr-1" /> Hapus</Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Tambah"} FAQ</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="faq-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Pertanyaan</Label><Input value={form.question} onChange={(e) => set("question", e.target.value)} className="mt-1.5" data-testid="faq-question" /></div>
            <div><Label>Jawaban</Label><Textarea value={form.answer} onChange={(e) => set("answer", e.target.value)} rows={4} className="mt-1.5" data-testid="faq-answer" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="save-faq">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
